/* global window, URLSearchParams */
import React, { useState, useMemo } from 'react';
import {
    Card, Table, Select, DatePicker, Tag, Space, Button, Alert, Typography, Row, Col, Statistic, Tooltip,
} from 'antd';
import {
    ReloadOutlined, ThunderboltOutlined, DollarOutlined, BookOutlined, WarningOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import { Column } from '@ant-design/charts';
import dayjs from 'dayjs';
import useAiUsage from './useAiUsage';
import { FEATURE_BY_KEY } from '../access-control/featureAccess';
import '../dashboard/Dashboard.css';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const money = (v, dp = 4) => `$${Number(v || 0).toFixed(dp)}`;
const int = (v) => Number(v || 0).toLocaleString();
const dim = <span style={{ color: '#bfbfbf' }}>—</span>;

const RANGES = {
    'Last 7 days': [dayjs().subtract(6, 'day'), dayjs()],
    'Last 30 days': [dayjs().subtract(29, 'day'), dayjs()],
    'Last 90 days': [dayjs().subtract(89, 'day'), dayjs()],
    'This month': [dayjs().startOf('month'), dayjs()],
    'Last month': [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')],
};

const changeTag = (pct) => {
    if (pct === null || pct === undefined) return null;
    const up = pct > 0;
    return <Tag color={up ? 'volcano' : 'green'} style={{ marginLeft: 8 }}>{up ? '+' : ''}{pct}% vs prior</Tag>;
};

// Platform-admin view of every model call (ADR-033): what it cost, which
// company and feature drove it, and how the intent fallback is resolving.
const AiUsage = () => {
    const presetCompany = useMemo(() => {
        try { return new URLSearchParams(window.location.search).get('company') || undefined; } catch { return undefined; }
    }, []);
    const [range, setRange] = useState(RANGES['Last 30 days']);
    const [companyId, setCompanyId] = useState(presetCompany);
    const [groupBy, setGroupBy] = useState('auto');

    const filters = useMemo(() => ({
        startDate: range && range[0] ? range[0].format('YYYY-MM-DD') : undefined,
        endDate: range && range[1] ? range[1].format('YYYY-MM-DD') : undefined,
        timezone: dayjs.tz ? dayjs.tz.guess() : 'UTC',
        groupBy,
        companyId,
    }), [range, groupBy, companyId]);

    const { data, loading, error, companies, refetch } = useAiUsage(filters);
    const k = (data && data.kpis) || {};

    const trendData = useMemo(() => {
        const rows = (data && data.trend) || [];
        return rows.flatMap((r) => ([
            { bucket: r.bucket, type: 'Model calls', count: r.calls },
            { bucket: r.bucket, type: 'Resolved without a model', count: r.no_model_events },
        ]));
    }, [data]);

    // G2 5.x config — same shape as dashboard/index.js messagingColumnConfig
    const trendConfig = {
        data: trendData,
        xField: 'bucket',
        yField: 'count',
        colorField: 'type',
        group: true,
        style: { maxWidth: 24 },
        scale: {
            y: { nice: true, tickCount: 5 },
            color: { range: ['#1890ff', '#38b549'], domain: ['Model calls', 'Resolved without a model'] },
        },
        axis: {
            x: { labelFontSize: 11, labelFill: '#666' },
            y: {
                labelFontSize: 11, labelFill: '#666', labelFormatter: (v) => (Number.isInteger(v) ? String(v) : ''), grid: true, gridStroke: '#f0f0f0',
            },
        },
        legend: { color: { title: false, position: 'top' } },
        height: 220,
        autoFit: true,
    };

    const tiles = [
        { title: 'Model calls', value: int(k.calls), icon: <ThunderboltOutlined />, color: '#1677ff', extra: changeTag(k.calls_change_pct) },
        { title: 'Estimated cost', value: money(k.cost_usd, 2), icon: <DollarOutlined />, color: '#52c41a', extra: changeTag(k.cost_change_pct) },
        { title: 'Resolved without a model', value: int(k.no_model_events), icon: <BookOutlined />, color: '#722ed1', hint: 'Dictionary, normalisation and duplicate-step resolutions — zero cost.' },
        { title: 'Error rate', value: `${k.error_rate_pct || 0}%`, icon: <WarningOutlined />, color: k.failures ? '#faad14' : '#8c8c8c', hint: `${int(k.failures)} failed or timed out` },
        { title: 'Latency p50 / p95', value: k.p50_latency_ms === null || k.p50_latency_ms === undefined ? '—' : `${int(k.p50_latency_ms)} / ${int(k.p95_latency_ms)} ms`, icon: <ClockCircleOutlined />, color: '#13c2c2' },
    ];

    const featureLabel = (key) => (FEATURE_BY_KEY[key] && FEATURE_BY_KEY[key].label) || key;

    const companyColumns = [
        { title: 'Company', dataIndex: 'company_name', key: 'company_name', ellipsis: true },
        {
            title: 'Licensed AI',
            key: 'licensed',
            render: (_, r) => (r.licensed_ai_features && r.licensed_ai_features.length
                ? r.licensed_ai_features.map((f) => <Tag color="purple" key={f}>{featureLabel(f)}</Tag>)
                : dim),
        },
        {
            title: 'Controls',
            key: 'controls',
            render: (_, r) => {
                const s = r.settings || [];
                if (!s.length) return dim;
                return s.map((x) => (
                    <Tag key={x.feature}>
                        {featureLabel(x.feature)}: {x.monthly_call_cap !== null && x.monthly_call_cap !== undefined ? `cap ${int(x.monthly_call_cap)}` : 'no cap'}
                    </Tag>
                ));
            },
        },
        { title: 'Model calls', dataIndex: 'calls', key: 'calls', align: 'right', render: int, sorter: (a, b) => a.calls - b.calls },
        { title: 'No-model', dataIndex: 'no_model_events', key: 'nm', align: 'right', render: int, sorter: (a, b) => a.no_model_events - b.no_model_events },
        { title: 'Cost', dataIndex: 'cost_usd', key: 'cost', align: 'right', render: (v) => money(v), sorter: (a, b) => a.cost_usd - b.cost_usd, defaultSortOrder: 'descend' },
        { title: 'Failures', dataIndex: 'failures', key: 'failures', align: 'right', render: (v) => (v ? <Text type="danger">{int(v)}</Text> : dim) },
        { title: 'Capped', dataIndex: 'capped_events', key: 'capped', align: 'right', render: (v) => (v ? <Text type="warning">{int(v)}</Text> : dim) },
    ];

    const featureColumns = [
        { title: 'Feature', dataIndex: 'feature', key: 'feature', render: (v, r) => <span>{featureLabel(v)} {r.licensable ? <Tag color="purple" style={{ marginLeft: 4 }}>licensable</Tag> : <Tag style={{ marginLeft: 4 }}>built-in</Tag>}</span> },
        { title: 'Model calls', dataIndex: 'calls', key: 'calls', align: 'right', render: int },
        { title: 'No-model', dataIndex: 'no_model_events', key: 'nm', align: 'right', render: int },
        { title: 'Tokens in / out', key: 'tok', align: 'right', render: (_, r) => `${int(r.input_tokens)} / ${int(r.output_tokens)}` },
        { title: 'Cost', dataIndex: 'cost_usd', key: 'cost', align: 'right', render: (v) => money(v) },
        { title: 'Failures', dataIndex: 'failures', key: 'failures', align: 'right', render: (v) => (v ? <Text type="danger">{int(v)}</Text> : dim) },
    ];

    const modelColumns = [
        { title: 'Provider', dataIndex: 'provider', key: 'provider', width: 100 },
        { title: 'Model', dataIndex: 'model', key: 'model', render: (v) => v || dim },
        { title: 'Calls', dataIndex: 'calls', key: 'calls', align: 'right', render: int },
        { title: 'Tokens in / out', key: 'tok', align: 'right', render: (_, r) => `${int(r.input_tokens)} / ${int(r.output_tokens)}` },
        { title: 'Cost', dataIndex: 'cost_usd', key: 'cost', align: 'right', render: (v) => money(v) },
        {
            title: 'Unpriced',
            dataIndex: 'unpriced_calls',
            key: 'unpriced',
            align: 'right',
            render: (v) => (v ? <Tooltip title="Model not in AI_PRICING_JSON — cost shows as null, never zero"><Text type="warning">{int(v)}</Text></Tooltip> : dim),
        },
    ];

    const failureColumns = [
        { title: 'Time', dataIndex: 'created_at', key: 't', width: 170, render: (t) => dayjs(t).format('MMM D, h:mm:ss A') },
        { title: 'Company', dataIndex: 'company_name', key: 'c', ellipsis: true, render: (v) => v || dim },
        { title: 'Feature', dataIndex: 'feature', key: 'f', render: featureLabel },
        { title: 'Model', key: 'm', render: (_, r) => `${r.provider} · ${r.model || '—'}` },
        { title: 'Status', dataIndex: 'status', key: 's', width: 90, render: (s) => <Tag color={s === 'timeout' ? 'orange' : 'red'}>{s}</Tag> },
        { title: 'Error', dataIndex: 'error', key: 'e', ellipsis: true },
    ];

    const funnel = (data && data.intentFunnel) || {};
    const funnelRows = Object.entries(funnel).sort((a, b) => b[1] - a[1]).map(([outcome, count]) => ({ outcome, count }));

    return (
        <div className="gh-users-page" style={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <Title level={4} style={{ margin: 0 }}><ThunderboltOutlined style={{ marginRight: 8 }} />AI Usage</Title>
                <Space wrap>
                    <Select
                        allowClear
                        showSearch
                        placeholder="All companies"
                        style={{ minWidth: 220 }}
                        value={companyId}
                        onChange={setCompanyId}
                        optionFilterProp="label"
                        options={companies.map((c) => ({ label: c.name, value: c.id }))}
                    />
                    <RangePicker value={range} onChange={setRange} presets={Object.entries(RANGES).map(([label, value]) => ({ label, value }))} allowClear={false} />
                    <Select value={groupBy} onChange={setGroupBy} style={{ width: 110 }} options={[{ value: 'auto', label: 'Auto' }, { value: 'day', label: 'Daily' }, { value: 'week', label: 'Weekly' }, { value: 'month', label: 'Monthly' }]} />
                    <Button icon={<ReloadOutlined />} onClick={refetch} loading={loading}>Refresh</Button>
                </Space>
            </div>

            {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}
            {k.unpriced_calls > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={`${int(k.unpriced_calls)} model call${k.unpriced_calls === 1 ? '' : 's'} used a model that is not in AI_PRICING_JSON — their cost is excluded from every total here.`}
                />
            )}

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                {tiles.map((tile) => (
                    <Col xs={12} md={8} xl={Math.floor(24 / tiles.length)} key={tile.title}>
                        <Card className="gh-dash-card" bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={tile.hint ? <Tooltip title={tile.hint}><span>{tile.title}</span></Tooltip> : tile.title}
                                value={tile.value}
                                prefix={React.cloneElement(tile.icon, { style: { color: tile.color } })}
                                suffix={tile.extra}
                            />
                        </Card>
                    </Col>
                ))}
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} lg={16}>
                    <Card className="gh-dash-card" title="Model calls vs. resolved without a model" loading={loading && !data}>
                        {trendData.length ? <Column {...trendConfig} /> : <Text type="secondary">No activity in this range.</Text>}
                    </Card>
                </Col>
                <Col xs={24} lg={8}>
                    <Card className="gh-dash-card" title="Reply understanding — how replies resolved" loading={loading && !data}>
                        {funnelRows.length ? (
                            <Table
                                size="small"
                                pagination={false}
                                rowKey="outcome"
                                dataSource={funnelRows}
                                columns={[
                                    { title: 'Outcome', dataIndex: 'outcome', key: 'o', render: (v) => <Tag color={/model/.test(v) ? 'blue' : (v === 'unresolved' ? 'default' : 'green')}>{v}</Tag> },
                                    { title: 'Events', dataIndex: 'count', key: 'c', align: 'right', render: int },
                                ]}
                            />
                        ) : <Text type="secondary">No "I don't understand" events in this range.</Text>}
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                            normalized / dictionary / previous-step cost nothing; model-* are Bedrock calls; unresolved fell back to the re-prompt.
                        </Text>
                    </Card>
                </Col>
            </Row>

            <Card className="gh-dash-card" title="By company" style={{ marginBottom: 16 }} extra={<Text type="secondary" style={{ fontSize: 12 }}>Enable / pause / cap on the Companies page → AI card</Text>}>
                <Table className="gh-dash-interview-table" size="small" rowKey={(r) => r.company_id || 'none'} loading={loading} columns={companyColumns} dataSource={(data && data.byCompany) || []} pagination={{ pageSize: 25, showSizeChanger: false }} />
            </Card>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} lg={12}>
                    <Card className="gh-dash-card" title="By feature">
                        <Table size="small" rowKey="feature" loading={loading} columns={featureColumns} dataSource={(data && data.byFeature) || []} pagination={false} />
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card className="gh-dash-card" title="By model">
                        <Table size="small" rowKey={(r) => `${r.provider}:${r.model}`} loading={loading} columns={modelColumns} dataSource={(data && data.byModel) || []} pagination={false} />
                    </Card>
                </Col>
            </Row>

            <Card className="gh-dash-card" title="Recent failures">
                <Table size="small" rowKey={(r, i) => `${r.created_at}-${i}`} loading={loading} columns={failureColumns} dataSource={(data && data.recentFailures) || []} pagination={false} locale={{ emptyText: 'No failed or timed-out calls in this range' }} />
            </Card>
        </div>
    );
};

export default AiUsage;
